// useModelPicker.ts — state for the composer's model picker.
//
// `models` is the inference fleet's served ids (default first). The picker
// can be pointed at two things:
//   - a selected session — switching PATCHes the session and the next turn
//     goes out under the new model, transcript intact;
//   - nothing selected — it picks the model the next New session gets.
// The new-session choice is persisted to localStorage so it survives
// reloads and back-to-back creates. The pure rules (width, revert, which
// remembered choice survives) live in modelPicker.ts.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { modelsApi, sessionsApi } from '../api';
import type { Session } from '../api';
import {
  activeModelOf,
  fleetDefault,
  modelWidthCh,
  rememberedModel,
  settleModelDraft,
} from './modelPicker';
import { replaceById } from './sessionRows';

const NEW_MODEL_KEY = 'thirdeye.sessions.newModel';

export interface ModelPickerArgs {
  /** The open session, or null when the picker governs the next New. */
  selected: Session | null;
  setSessions: React.Dispatch<React.SetStateAction<Session[]>>;
  setStreamError: (msg: string) => void;
}

export interface ModelPicker {
  /** The fleet's served ids; empty hides the picker. */
  models: string[];
  /** The model the next New session is created with. */
  newModel: string;
  /** The model in force for whatever the picker points at. */
  activeModel: string;
  /** What is typed in the box — may be a partial filter. */
  modelDraft: string;
  /** A switch on the open session is in flight. */
  modelSwitching: boolean;
  /** The box's width in `ch`. */
  widthCh: number;
  onDraftChange: (next: string) => void;
  onFocus: () => void;
  onBlur: () => void;
}

export function useModelPicker({ selected, setSessions, setStreamError }: ModelPickerArgs): ModelPicker {
  const [models, setModels] = useState<string[]>([]);
  // The fleet default, shown for legacy session rows that predate the
  // picker and so have no model pinned.
  const [defaultModel, setDefaultModel] = useState<string>('');
  const [newModel, setNewModel] = useState<string>(() => {
    try { return localStorage.getItem(NEW_MODEL_KEY) || ''; }
    catch { return ''; }
  });
  useEffect(() => {
    if (!newModel) return;
    try { localStorage.setItem(NEW_MODEL_KEY, newModel); }
    catch { /* ignore */ }
  }, [newModel]);
  // What's currently typed in the combobox, which is not the same thing as
  // the model in force: only an exact hit on a served id commits, anything
  // else is reverted on blur.
  const [modelDraft, setModelDraft] = useState<string>('');
  const [modelSwitching, setModelSwitching] = useState(false);

  const activeModel = activeModelOf(selected, defaultModel, newModel);

  // Snap the box back to the model in force whenever that changes out from
  // under it: a different session selected, the fleet list landing, a
  // switch completing.
  useEffect(() => { setModelDraft(activeModel); }, [activeModel]);

  const widthCh = useMemo(() => modelWidthCh(models, activeModel), [models, activeModel]);

  // commitModel is reached only with an id the fleet serves — either
  // clicked in the popover or typed out in full.
  const commitModel = useCallback(async (model: string) => {
    if (model === activeModel) return;
    if (!selected) {
      console.debug('[sessions] model for the next new session:', model);
      setNewModel(model);
      return;
    }
    console.debug('[sessions] switching session model', { session: selected.id, model });
    setModelSwitching(true);
    try {
      const updated = await sessionsApi.setModel(selected.id, model);
      // Splice rather than refetch: a full list reload here would race the
      // turn-end title regen and flicker the row labels.
      setSessions((prev) => replaceById(prev, updated));
    } catch (err) {
      setStreamError(`could not switch model: ${err}`);
      setModelDraft(activeModel);
    } finally {
      setModelSwitching(false);
    }
  }, [activeModel, selected, setSessions, setStreamError]);

  // Load the model list once. Soft-fail: if inference is unreachable the
  // picker just hides (new sessions take the server default), rather than
  // blocking the page.
  useEffect(() => {
    let alive = true;
    modelsApi.list()
      .then(({ models: ms, default: def }) => {
        if (!alive) return;
        setModels(ms);
        setDefaultModel(fleetDefault(ms, def));
        setNewModel((cur) => rememberedModel(cur, ms, def));
      })
      .catch((err) => console.error('Failed to list models:', err));
    return () => { alive = false; };
  }, []);

  return {
    models,
    newModel,
    activeModel,
    modelDraft,
    modelSwitching,
    widthCh,
    // Fires for both a typed character and a picked suggestion; an exact
    // hit on a served id is the signal that it was a pick, not a search.
    onDraftChange: (next) => {
      setModelDraft(next);
      if (models.includes(next)) commitModel(next);
    },
    // Clear on focus so the popover opens on the whole fleet. Leaving the
    // current id in the box would filter the list down to that one model.
    onFocus: () => setModelDraft(''),
    // Abandoning a half-typed filter must not leave the box claiming a
    // model the session isn't running.
    onBlur: () => {
      window.setTimeout(() => setModelDraft((cur) => settleModelDraft(cur, models, activeModel)), 150);
    },
  };
}
